// Lets a person show a tile's video full screen or in picture-in-picture, in the browsers that
// can; the person's sound goes on playing through the page.
//
// Showing a tile full screen or picture-in-picture, kept apart from the rest of Collab: it knows
// only browser APIs. A tile plays its person's MediaStream in a <video playsinline>, the element an
// iPhone shows full screen or picture-in-picture, so that <video> itself is shown, its picture alone,
// and stays in the tile when the person returns it to the page.

export type Presentation = 'fullscreen' | 'picture-in-picture';

type WebKitVideo = HTMLVideoElement & {
  webkitSetPresentationMode?(mode: 'inline' | Presentation): void;
  webkitPresentationMode?: 'inline' | Presentation;
};

/** How long the picture may take to play and the browser to show it, within the click's 5 s of activation. */
const WAIT = 4_000;

/** Resolves at the first `type` event on `target` after which `done()` holds. */
function when(target: EventTarget, type: string, done: () => boolean = () => true): Promise<void> {
  return new Promise(resolve => {
    const listener = () => {
      if (!done()) return;
      target.removeEventListener(type, listener);
      resolve();
    };
    target.addEventListener(type, listener);
  });
}

/** `promise`, or a failure saying `message` after WAIT. */
function within<T>(promise: Promise<T>, message: string): Promise<T> {
  return Promise.race([promise, new Promise<never>((_, reject) => setTimeout(() => reject(new Error(message)), WAIT))]);
}

/** The presentations this browser offers. */
export function offered(): ReadonlySet<Presentation> {
  const webkit = 'webkitSetPresentationMode' in HTMLVideoElement.prototype;
  const offers = new Set<Presentation>();
  if (document.fullscreenEnabled || webkit) offers.add('fullscreen');
  if (document.pictureInPictureEnabled || webkit) offers.add('picture-in-picture');
  return offers;
}

/** Shows `video` `how`, until the person returns it to the page. */
async function show(video: WebKitVideo, how: Presentation): Promise<void> {
  if (video.webkitSetPresentationMode) {
    // Safari's presentation modes take the click's activation after awaits, and work on an iPhone,
    // which offers no standard full screen; its requestPictureInPicture took it only from 2026-07.
    const shown = when(video, 'webkitpresentationmodechanged', () => video.webkitPresentationMode === how);
    video.webkitSetPresentationMode(how);
    await within(shown, `The browser did not show it (${how}).`);
    return when(video, 'webkitpresentationmodechanged', () => video.webkitPresentationMode === 'inline');
  }
  if (how === 'fullscreen') {
    await video.requestFullscreen();
    return when(document, 'fullscreenchange', () => document.fullscreenElement !== video);
  }
  await video.requestPictureInPicture();
  return when(video, 'leavepictureinpicture');
}

/**
 * Shows a tile's `video` `how` until the person returns it to the page, then resolves; rejects when
 * it cannot start, as when it has no picture. Call it inside the click on the button that asks for it.
 */
export async function present(video: WebKitVideo, how: Presentation): Promise<void> {
  if (video.readyState < HTMLMediaElement.HAVE_METADATA) await within(when(video, 'loadedmetadata'), 'No picture came.');
  await show(video, how);
}
